/**
 * /memory-compactor — Engine 23 · Conversation Memory Compactor.
 *
 * World-first (Jun 2026): quantifies the O(N²) chat-history resend tax and
 * prescribes the keep-last-N + rolling-summary policy that kills it.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessagesSquare, RefreshCw, AlertCircle, ArrowRight, Layers } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, sub, color = 'text-white', testId }) => (
  <div className="rounded-xl border border-white/10 bg-slate-900/50 backdrop-blur-xl shadow-sm p-5" data-testid={testId}>
    <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-1">{label}</div>
    <div className={`text-3xl font-extrabold tabular-nums font-mono ${color}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const MODELS = ['gpt-4o', 'gpt-4o-mini', 'claude-sonnet', 'claude-haiku', 'gemini-flash', 'mistral'];

const Num = ({ label, value, onChange, min, max, testId }) => (
  <label className="text-xs text-slate-400 flex flex-col gap-1">
    {label}
    <input type="number" value={value} min={min} max={max}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-white/15 px-2 py-1.5 text-sm bg-slate-900 tabular-nums text-slate-100"
      data-testid={testId} />
  </label>
);

const MemoryCompactor = () => {
  const [turns, setTurns] = useState(20);
  const [turnTokens, setTurnTokens] = useState(200);
  const [sysTokens, setSysTokens] = useState(800);
  const [model, setModel] = useState('gpt-4o');
  const [monthlyConvos, setMonthlyConvos] = useState(5000);
  const [keepN, setKeepN] = useState(6);
  const [sim, setSim] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  const [scan, setScan] = useState(null);
  const [scanErr, setScanErr] = useState(false);
  const [scanLoading, setScanLoading] = useState(true);

  const auth = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token') || ''}`, 'Content-Type': 'application/json' });

  const runSim = async () => {
    setSimLoading(true);
    try {
      const r = await fetch(`${API}/memory-compactor/simulate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          turns: Number(turns) || 2, avg_turn_tokens: Number(turnTokens) || 10,
          system_prompt_tokens: Number(sysTokens) || 0, model,
          monthly_conversations: Number(monthlyConvos) || 1, keep_last_n: Number(keepN) || 1,
        }),
      });
      if (r.ok) setSim(await r.json());
    } finally {
      setSimLoading(false);
    }
  };

  const fetchScan = async () => {
    setScanLoading(true);
    try {
      const r = await fetch(`${API}/memory-compactor/scan?days=30`, { headers: auth() });
      if (!r.ok) throw new Error('unauthorized');
      setScan(await r.json());
      setScanErr(false);
    } catch (e) {
      setScanErr(true);
    } finally {
      setScanLoading(false);
    }
  };

  useEffect(() => { runSim(); fetchScan(); /* eslint-disable-next-line */ }, []);

  const barPct = sim ? Math.max(4, Math.round(100 * sim.compacted_input_tokens / sim.naive_input_tokens)) : 0;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="memory-compactor-page">
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8">
          <Badge className="bg-sky-500/10 border-sky-400/40 text-sky-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-3" data-testid="mc-badge">
            <MessagesSquare className="w-3 h-3 mr-1.5 inline" /> Engine 23 · Memory Compactor · world-first
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight"><BrandTitle>Conversation Memory Compactor</BrandTitle></h1>
          <p className="text-slate-300 mt-2 max-w-2xl">
            Every multi-turn chat resends the <em>entire history</em> on every call — a 40-turn conversation pays for
            turn 1 <span className="text-sky-300 font-semibold">forty times</span>. The cost grows quadratically.
            This engine quantifies the resend tax and prescribes the pruning policy that kills 40-70% of it.
          </p>
        </div>

        {/* Simulator */}
        <Card className="bg-slate-900/50 backdrop-blur-xl border-white/10 mb-10">
          <CardContent className="p-6">
            <h2 className="text-base font-bold text-white mb-4">Simulate your conversation shape</h2>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
              <Num label="Turns" value={turns} onChange={setTurns} min={2} max={500} testId="mc-input-turns" />
              <Num label="Tokens / turn" value={turnTokens} onChange={setTurnTokens} min={10} max={50000} testId="mc-input-turn-tokens" />
              <Num label="System prompt" value={sysTokens} onChange={setSysTokens} min={0} max={100000} testId="mc-input-sys-tokens" />
              <label className="text-xs text-slate-400 flex flex-col gap-1">
                Model
                <select value={model} onChange={(e) => setModel(e.target.value)} className="rounded-lg border border-white/15 px-2 py-1.5 text-sm bg-slate-900 text-slate-100" data-testid="mc-model-select">
                  {MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <Num label="Convos / month" value={monthlyConvos} onChange={setMonthlyConvos} min={1} max={100000000} testId="mc-input-monthly" />
              <Num label="Keep last N" value={keepN} onChange={setKeepN} min={1} max={100} testId="mc-input-keepn" />
            </div>
            <button onClick={runSim} disabled={simLoading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white px-4 py-1.5 text-sm font-semibold mb-5"
              data-testid="mc-simulate-btn">
              {simLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <MessagesSquare className="w-3.5 h-3.5" />} Recalculate
            </button>

            {sim && (
              <div data-testid="mc-sim-result">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                  <Stat label="Naive cost / convo" value={`$${sim.naive_cost_per_conversation_usd}`} sub={`${sim.naive_input_tokens.toLocaleString()} input tokens`} testId="mc-stat-naive" />
                  <Stat label="Compacted / convo" value={`$${sim.compacted_cost_per_conversation_usd}`} color="text-sky-300" sub={`${sim.compacted_input_tokens.toLocaleString()} input tokens`} testId="mc-stat-compacted" />
                  <Stat label="Savings" value={`${sim.savings_pct}%`} color="text-emerald-300" sub={`from turn ${sim.breakeven_turn} onward`} testId="mc-stat-savings-pct" />
                  <Stat label="Monthly savings" value={`$${sim.projected_monthly_savings_usd.toLocaleString()}`} color="text-emerald-300" sub={`at ${Number(monthlyConvos).toLocaleString()} convos/mo`} testId="mc-stat-monthly" />
                </div>
                {/* naive vs compacted bar */}
                <div className="space-y-2" data-testid="mc-sim-bars">
                  <div className="flex items-center gap-3">
                    <span className="w-24 text-[11px] font-mono text-slate-500 text-right">NAIVE</span>
                    <div className="flex-1 h-6 rounded bg-slate-800 overflow-hidden">
                      <div className="h-full bg-rose-500/70" style={{ width: '100%' }} />
                    </div>
                    <span className="w-28 text-xs tabular-nums text-rose-300">{sim.naive_input_tokens.toLocaleString()} tok</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-24 text-[11px] font-mono text-slate-500 text-right">COMPACTED</span>
                    <div className="flex-1 h-6 rounded bg-slate-800 overflow-hidden">
                      <div className="h-full bg-emerald-500/80 transition-all duration-500" style={{ width: `${barPct}%` }} />
                    </div>
                    <span className="w-28 text-xs tabular-nums text-emerald-300">{sim.compacted_input_tokens.toLocaleString()} tok</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Authed scan */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-white">Your live history-bloat scan (last 30 days)</h2>
          <button onClick={fetchScan} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 text-sm font-semibold" data-testid="mc-scan-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${scanLoading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
        {scanErr ? (
          <div className="rounded-xl border border-white/10 bg-slate-900/50 backdrop-blur-xl px-6 py-10 text-center text-slate-500" data-testid="mc-scan-unauth">
            <AlertCircle className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            Sign in and route traffic through the <Link to="/engine-27" className="text-emerald-400 hover:underline">Auto-Compiler Proxy</Link> to see your real resend-tax numbers.
          </div>
        ) : (
          <div className="rounded-2xl bg-slate-900/60 border border-white/10 overflow-hidden">
            {(!scan?.rows || scan.rows.length === 0) ? (
              <div className="px-6 py-10 text-center text-slate-500" data-testid="mc-scan-empty">
                <AlertCircle className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                No proxy traffic yet. Point your base_url at the Auto-Compiler Proxy and this fills itself.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="mc-scan-table">
                <thead className="bg-slate-900/60 border-b border-white/10">
                  <tr>
                    <th className="text-left px-6 py-3 font-semibold text-slate-300">Model</th>
                    <th className="text-right px-3 py-3 font-semibold text-slate-300">Calls</th>
                    <th className="text-right px-3 py-3 font-semibold text-slate-300">Avg prompt</th>
                    <th className="text-right px-3 py-3 font-semibold text-slate-300">Bloat %</th>
                    <th className="text-right px-6 py-3 font-semibold text-slate-300">Prunable $</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {scan.rows.map((r, i) => (
                    <tr key={r.model} className="hover:bg-slate-900/60" data-testid={`mc-scan-row-${i}`}>
                      <td className="px-6 py-3 font-mono text-emerald-300">{r.model}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-200">{r.calls}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-200">{r.avg_prompt_tokens}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-amber-300">{r.history_bloat_share_pct}%</td>
                      <td className="px-6 py-3 text-right tabular-nums font-semibold text-sky-300">${r.est_prunable_waste_usd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Educational footer */}
        <Card className="bg-slate-900/50 backdrop-blur-xl border-white/10 mt-8">
          <CardContent className="p-5">
            <h3 className="font-bold text-white flex items-center gap-2 mb-2"><Layers className="w-4 h-4 text-sky-400" /> The quadratic tax, explained</h3>
            <p className="text-sm text-slate-200 leading-relaxed">
              A T-turn conversation with t tokens per turn and an S-token system prompt costs
              <span className="font-mono text-sky-300"> T·S + t·T(T+1)/2 </span> input tokens if you resend full history — that's
              <strong> quadratic in conversation length</strong>. With keep-last-N + a 150-token rolling summary the cost becomes
              linear. On a 40-turn support conversation at 200 tokens/turn, that's the difference between ~196K and ~78K input
              tokens — a 60% cut, on every long conversation, forever.
            </p>
            <p className="text-xs text-slate-500 mt-2 italic">
              First-to-market: as of June 2026 no FinOps or LLM-observability tool quantifies the history resend tax.
            </p>
            <Link to="/output-diet" className="inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 mt-3 font-semibold" data-testid="mc-link-output-diet">
              Next: put your responses on a diet <ArrowRight className="w-4 h-4" />
            </Link>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default MemoryCompactor;
